/**
 * Theme Manager Service
 * Handles switching between dark and light themes for the POS application
 */

export class ThemeManager {
  private currentTheme: 'dark' | 'light' = 'dark';
  private themeChangeCallbacks: Array<(theme: 'dark' | 'light') => void> = [];

  constructor() {
    // Load saved theme preference
    const savedTheme = localStorage.getItem('pos-theme') as 'dark' | 'light';
    if (savedTheme && (savedTheme === 'dark' || savedTheme === 'light')) {
      this.currentTheme = savedTheme;
    }

    // Apply initial theme
    this.applyTheme(this.currentTheme);
  }

  /**
   * Set the current theme
   */
  setTheme(theme: 'dark' | 'light'): void {
    if (this.currentTheme !== theme) {
      this.currentTheme = theme;
      this.applyTheme(theme);
      localStorage.setItem('pos-theme', theme);

      // Notify all callbacks
      this.themeChangeCallbacks.forEach((callback) => callback(theme));
    }
  }

  /**
   * Get the current theme
   */
  getTheme(): 'dark' | 'light' {
    return this.currentTheme;
  }

  /**
   * Toggle between dark and light themes
   */
  toggleTheme(): void {
    const newTheme = this.currentTheme === 'dark' ? 'light' : 'dark';
    this.setTheme(newTheme);
  }

  /**
   * Check if currently in light mode
   */
  isLightMode(): boolean {
    return this.currentTheme === 'light';
  }

  /**
   * Subscribe to theme changes
   */
  onThemeChange(callback: (theme: 'dark' | 'light') => void): () => void {
    this.themeChangeCallbacks.push(callback);

    // Return unsubscribe function
    return () => {
      const index = this.themeChangeCallbacks.indexOf(callback);
      if (index > -1) {
        this.themeChangeCallbacks.splice(index, 1);
      }
    };
  }

  /**
   * Apply theme to the document
   */
  private applyTheme(theme: 'dark' | 'light'): void {
    const body = document.body;

    if (theme === 'light') {
      body.classList.add('light-mode');
    } else {
      body.classList.remove('light-mode');
    }
  }
}

// Create singleton instance
export const themeManager = new ThemeManager();
